interface SectionTitleProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  tone?: 'light' | 'dark';
  align?: 'center' | 'left';
  className?: string;
}

/**
 * Section heading with an optional eyebrow label and gradient underline.
 * `tone="dark"` makes it legible on dark backgrounds (fixes the old bug where
 * a hardcoded gray heading rendered nearly invisible on dark sections).
 */
const SectionTitle = ({
  title,
  subtitle,
  eyebrow,
  tone = 'light',
  align = 'center',
  className = '',
}: SectionTitleProps) => {
  const isDark = tone === 'dark';
  const isCenter = align === 'center';

  return (
    <div
      className={`mb-14 max-w-3xl ${isCenter ? 'mx-auto text-center' : 'text-left'} ${className}`}
    >
      {eyebrow && (
        <span
          className={`mb-4 inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest ${
            isDark
              ? 'bg-white/10 text-accent-400'
              : 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
          }`}
        >
          {eyebrow}
        </span>
      )}
      <h2
        className={`text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl ${
          isDark ? 'text-white' : 'text-content'
        }`}
      >
        {title}
      </h2>
      <div
        className={`mt-6 h-1 w-20 rounded-full bg-gradient-to-r from-brand-500 to-accent-400 ${
          isCenter ? 'mx-auto' : ''
        }`}
      />
      {subtitle && (
        <p
          className={`mt-6 text-lg leading-relaxed ${
            isDark ? 'text-slate-300' : 'text-content-muted'
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default SectionTitle;
